/**
 * GachaScreen Component
 *
 * Allows users to roll for new Pokemon using Primos (currency).
 * Cost: 100 Primos per roll, 1000 Primos for 10-roll
 * Features Limit Break system for duplicate Pokemon.
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowLeft, CircleDot, Star, X, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../contexts/GameContext';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import {
  generatePokemonSprite,
  getRarityColor,
  getAptitudeColor
} from '../utils/gameUtils';
import { TypeBadge, TypeIcon } from '../components/TypeIcon';
import { POKEMON, GACHA_RARITY, GENERATIONS } from '../shared/gameData';
import LimitBreakDiamonds from '../components/LimitBreakDiamonds';
import TutorialOverlay from '../components/TutorialOverlay';
import GachaPoolInfoModal from '../components/GachaPoolInfoModal';
import { apiGachaPokemonSingle, apiGachaPokemonTen, apiGetGachaRates } from '../services/apiService';

// Limit break shard rewards by rarity
const LIMIT_BREAK_SHARD_REWARDS = {
  Common: 1,
  Uncommon: 3,
  Rare: 5,
  Legendary: 20
};

// Rarity border colors for multi-roll display
const RARITY_BORDER_STYLES = {
  Common: 'border-gray-400 bg-gray-50',
  Uncommon: 'border-green-500 bg-green-50',
  Rare: 'border-blue-500 bg-blue-50',
  Legendary: 'border-amber-500 bg-gradient-to-br from-amber-50 to-yellow-100'
};

const GachaScreen = () => {
  const { setGameState } = useGame();
  const { primos, setPrimos, refreshInventory, getPokemonLimitBreak } = useInventory();
  const { authToken } = useAuth();
  const { isTutorialStep, advanceStep, canUseFreePokemonPull, markFreePokemonPullUsed } = useTutorial();
  const [rollResult, setRollResult] = useState(null);
  const [multiRollResults, setMultiRollResults] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [showTutorialOverlay, setShowTutorialOverlay] = useState(false);
  const [hasPulledDuringTutorial, setHasPulledDuringTutorial] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState('gen1');
  const [showPoolInfo, setShowPoolInfo] = useState(false);
  const [gachaRates, setGachaRates] = useState(null);

  // Generation navigation helpers
  const generationOrder = ['gen1', 'gen2', 'gen3', 'gen4', 'gen5', 'gen6', 'gen7', 'gen8', 'gen9'];
  const currentGenIndex = generationOrder.indexOf(selectedGeneration);

  const navigateGeneration = (direction) => {
    const newIndex = currentGenIndex + direction;
    if (newIndex >= 0 && newIndex < generationOrder.length) {
      setSelectedGeneration(generationOrder[newIndex]);
    }
  };

  // Fetch gacha rates from backend on mount
  useEffect(() => {
    const fetchRates = async () => {
      const rates = await apiGetGachaRates();
      if (rates) {
        setGachaRates(rates.pokemon);
      }
    };
    fetchRates();
  }, []);

  // Tutorial: Show overlay for pokemon-gacha step
  useEffect(() => {
    if (isTutorialStep('pokemon-gacha')) {
      setShowTutorialOverlay(true);
    }
  }, [isTutorialStep]);

  // Tutorial: Advance to support-gacha after pulling
  useEffect(() => {
    if (isTutorialStep('pokemon-gacha') && hasPulledDuringTutorial && !rollResult && !multiRollResults) {
      console.log('[GachaScreen] Tutorial pull complete - advancing to support-gacha');
      advanceStep('support-gacha');
      setGameState('supportGacha');
    }
  }, [isTutorialStep, hasPulledDuringTutorial, rollResult, multiRollResults, advanceStep, setGameState]);

  // Handle single roll using secure server-side API
  const handleRoll = async () => {
    if (primos < 100 || isRolling) return;

    setIsRolling(true);

    // Call secure server-side gacha API with selected generation
    const response = await apiGachaPokemonSingle(authToken, false, selectedGeneration);

    if (response && response.success) {
      // Update primos from server response
      setPrimos(response.primos);
      // Refresh inventory to get updated pokemon data
      await refreshInventory();

      const result = response.result;
      setRollResult({
        pokemon: result.pokemonName,
        rarity: result.rarity,
        isDuplicate: result.isDuplicate || false,
        isMaxLimitBreak: result.isMaxLimitBreak || false,
        limitBreakLevel: result.limitBreakLevel || 0,
        shardsAwarded: result.shardsAwarded || 0
      });
      setMultiRollResults(null);
    }

    setIsRolling(false);
  };

  // Handle FREE 10-roll for tutorial (no cost)
  const handleFreeTutorialPull = async () => {
    if (!canUseFreePokemonPull() || isRolling) return;

    setIsRolling(true);
    markFreePokemonPullUsed(); // Mark free pull as used

    // Call secure server-side gacha API with isFree=true and selected generation
    const response = await apiGachaPokemonTen(authToken, true, selectedGeneration);

    if (response && response.success) {
      // Update primos from server response
      setPrimos(response.primos);
      // Refresh inventory to get updated pokemon data
      await refreshInventory();

      const results = response.results.map(r => ({
        pokemon: r.pokemonName,
        rarity: r.rarity,
        isDuplicate: r.isDuplicate || false,
        isMaxLimitBreak: r.isMaxLimitBreak || false,
        limitBreakLevel: r.limitBreakLevel || 0,
        shardsAwarded: r.shardsAwarded || 0
      }));
      setMultiRollResults(results);
    }

    setIsRolling(false);
    setRollResult(null);
  };

  // Handle 10-roll using secure server-side API
  const handleMultiRoll = async () => {
    if (primos < 1000 || isRolling) return;

    setIsRolling(true);

    // Call secure server-side gacha API with selected generation
    const response = await apiGachaPokemonTen(authToken, false, selectedGeneration);

    if (response && response.success) {
      // Update primos from server response
      setPrimos(response.primos);
      // Refresh inventory to get updated pokemon data
      await refreshInventory();

      const results = response.results.map(r => ({
        pokemon: r.pokemonName,
        rarity: r.rarity,
        isDuplicate: r.isDuplicate || false,
        isMaxLimitBreak: r.isMaxLimitBreak || false,
        limitBreakLevel: r.limitBreakLevel || 0,
        shardsAwarded: r.shardsAwarded || 0
      }));
      setMultiRollResults(results);
    }

    setIsRolling(false);
    setRollResult(null);
  };

  return (
    <div className="min-h-screen bg-pocket-bg p-4">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-10 bg-white shadow-card rounded-2xl mb-4 max-w-lg mx-auto"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setGameState('menu')}
            className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <CircleDot size={20} className="text-type-poison" />
            <span className="font-bold text-pocket-text">Pokemon Gacha</span>
          </div>
          <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
            <Sparkles size={14} className="text-amber-500" />
            <span className="text-pocket-text font-bold text-sm">{primos.toLocaleString()}</span>
          </div>
        </div>
      </motion.header>

      {/* Generation Selector */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-card p-4 mb-4 max-w-lg mx-auto"
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateGeneration(-1)}
            disabled={currentGenIndex === 0}
            className={`p-2 rounded-lg transition-all ${
              currentGenIndex === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-pocket-blue hover:bg-blue-50'
            }`}
          >
            <ChevronLeft size={24} />
          </button>

          <div className="text-center flex-1">
            <p className="text-xs text-pocket-text-light font-semibold uppercase tracking-wide">Region</p>
            <h3 className="text-xl font-bold text-pocket-text mt-1">
              {GENERATIONS[selectedGeneration]?.name || 'Kanto'}
            </h3>
          </div>

          <button
            onClick={() => navigateGeneration(1)}
            disabled={currentGenIndex === generationOrder.length - 1}
            className={`p-2 rounded-lg transition-all ${
              currentGenIndex === generationOrder.length - 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-pocket-blue hover:bg-blue-50'
            }`}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Pool Info Button */}
        <button
          onClick={() => setShowPoolInfo(true)}
          className="w-full mt-3 py-2 px-4 bg-blue-50 text-pocket-blue rounded-lg font-semibold text-sm hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
        >
          <Info size={16} />
          View Pokemon Pool
        </button>
      </motion.div>

      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-card overflow-hidden"
        >
          {/* Multi-Roll Results Display */}
          {multiRollResults ? (
            <div className="p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {/* Header */}
                <div className="text-center mb-4">
                  <h2 className="text-xl font-bold text-pocket-text">Gacha Results!</h2>
                  <p className="text-pocket-text-light text-sm">
                    {multiRollResults.filter(r => r.rarity === 'Legendary').length > 0 && (
                      <span className="text-amber-500 font-bold">
                        {multiRollResults.filter(r => r.rarity === 'Legendary').length} Legendary!
                      </span>
                    )}
                    {multiRollResults.filter(r => r.rarity === 'Rare').length > 0 && (
                      <span className="text-blue-500 font-bold ml-2">
                        {multiRollResults.filter(r => r.rarity === 'Rare').length} Rare
                      </span>
                    )}
                  </p>
                </div>

                {/* Grid of results - 2 rows of 5 like Uma Musume */}
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {multiRollResults.map((result, index) => (
                    <motion.div
                      key={index}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setSelectedPokemon(result.pokemon)}
                      className={`relative rounded-lg border-2 p-1 cursor-pointer hover:ring-2 hover:ring-pocket-blue transition-all ${RARITY_BORDER_STYLES[result.rarity]}`}
                    >
                      {/* Rarity badge */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                        <span
                          className="text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
                          style={{ backgroundColor: getRarityColor(result.rarity) }}
                        >
                          {result.rarity}
                        </span>
                      </div>

                      {/* NEW badge for non-duplicates */}
                      {!result.isDuplicate && (
                        <div className="absolute -top-1 -right-1 z-10">
                          <span className="bg-red-500 text-white text-[8px] font-bold px-1 rounded">NEW</span>
                        </div>
                      )}

                      {/* Pokemon Sprite */}
                      <div className="aspect-square flex items-center justify-center mt-1">
                        <div className="w-full h-full flex items-center justify-center scale-75">
                          {generatePokemonSprite(POKEMON[result.pokemon]?.primaryType || 'Normal', result.pokemon, 64, false)}
                        </div>
                      </div>

                      {/* Limit Break Diamonds */}
                      <div className="flex justify-center -mt-1">
                        <LimitBreakDiamonds level={getPokemonLimitBreak(result.pokemon)} size={6} />
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Summary */}
                <div className="bg-pocket-bg rounded-xl p-3 mb-4 text-xs">
                  <div className="flex justify-between text-pocket-text-light">
                    <span>New Pokemon:</span>
                    <span className="font-bold text-pocket-text">{multiRollResults.filter(r => !r.isDuplicate).length}</span>
                  </div>
                  <div className="flex justify-between text-pocket-text-light">
                    <span>Limit Breaks:</span>
                    <span className="font-bold text-green-600">{multiRollResults.filter(r => r.isDuplicate && !r.isMaxLimitBreak).length}</span>
                  </div>
                  {multiRollResults.some(r => r.isMaxLimitBreak) && (
                    <div className="flex justify-between text-pocket-text-light">
                      <span>Shards Earned:</span>
                      <span className="font-bold text-purple-600">
                        +{multiRollResults.filter(r => r.isMaxLimitBreak).reduce((sum, r) => sum + (r.shardsAwarded || LIMIT_BREAK_SHARD_REWARDS[r.rarity]), 0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <motion.button
                    whileHover={{ scale: primos >= 1000 && !isRolling ? 1.02 : 1 }}
                    whileTap={{ scale: primos >= 1000 && !isRolling ? 0.98 : 1 }}
                    onClick={async () => {
                      setMultiRollResults(null);
                      if (primos >= 1000) {
                        await handleMultiRoll();
                      }
                    }}
                    disabled={primos < 1000 || isRolling}
                    className="w-full pocket-btn-purple py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Star size={16} />
                    {isRolling ? 'Rolling...' : '10-Roll Again'}
                  </motion.button>
                  <button
                    onClick={() => {
                      setMultiRollResults(null);
                      if (isTutorialStep('pokemon-gacha')) {
                        setHasPulledDuringTutorial(true);
                      }
                    }}
                    className="w-full py-2 text-pocket-text-light font-semibold hover:text-pocket-text transition-colors text-sm"
                  >
                    {isTutorialStep('pokemon-gacha') ? 'Continue Tutorial' : 'Back'}
                  </button>
                </div>
              </motion.div>
            </div>
          ) : !rollResult ? (
            <div className="p-6">
              {/* Roll Info */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-type-poison/10 flex items-center justify-center">
                  <CircleDot size={32} className="text-type-poison" />
                </div>
                <h2 className="text-xl font-bold text-pocket-text mb-2">Roll for Pokemon</h2>
                <p className="text-pocket-text-light">100 Primos per roll • 1,000 for 10-roll</p>
              </div>

              {/* Rates Card */}
              <div className="bg-pocket-bg rounded-xl p-4 mb-6">
                <h3 className="font-bold text-pocket-text mb-3 text-sm">Drop Rates</h3>
                <div className="space-y-2">
                  {gachaRates ? (
                    Object.entries(gachaRates).map(([rarity, rate]) => (
                      <div key={rarity} className="flex justify-between items-center">
                        <span
                          className="font-semibold text-sm"
                          style={{ color: getRarityColor(rarity) }}
                        >
                          {rarity}
                        </span>
                        <span className="text-pocket-text-light text-sm">{(rate * 100).toFixed(0)}%</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-pocket-text-light text-sm">Loading rates...</div>
                  )}
                </div>
              </div>

              {/* Limit Break Info */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6 border border-purple-200">
                <h3 className="font-bold text-pocket-text mb-2 text-sm flex items-center gap-2">
                  <LimitBreakDiamonds level={4} size={10} />
                  Limit Break System
                </h3>
                <p className="text-pocket-text-light text-xs">
                  Duplicate Pokemon increase Limit Break level (+5% base stats per level).
                  Max level duplicates award Limit Break Shards!
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {/* FREE 10-Roll Button (Tutorial Only) */}
                {canUseFreePokemonPull() && (
                  <motion.button
                    whileHover={{ scale: !isRolling ? 1.02 : 1 }}
                    whileTap={{ scale: !isRolling ? 0.98 : 1 }}
                    onClick={handleFreeTutorialPull}
                    disabled={isRolling}
                    className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-white py-4 text-lg font-bold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Sparkles size={20} />
                    {isRolling ? 'Rolling...' : 'FREE 10-Roll!'}
                    <Sparkles size={20} />
                  </motion.button>
                )}

                {/* 10-Roll Button (Regular) */}
                {!canUseFreePokemonPull() && (
                  <motion.button
                    whileHover={{ scale: primos >= 1000 && !isRolling ? 1.02 : 1 }}
                    whileTap={{ scale: primos >= 1000 && !isRolling ? 0.98 : 1 }}
                    onClick={handleMultiRoll}
                    disabled={primos < 1000 || isRolling}
                    className="w-full pocket-btn-purple py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Star size={20} />
                    {isRolling ? 'Rolling...' : primos >= 1000 ? '10-Roll! (1,000)' : 'Need 1,000 Primos'}
                  </motion.button>
                )}

                {/* Single Roll Button */}
                {!canUseFreePokemonPull() && (
                  <motion.button
                    whileHover={{ scale: primos >= 100 && !isRolling ? 1.02 : 1 }}
                    whileTap={{ scale: primos >= 100 && !isRolling ? 0.98 : 1 }}
                    onClick={handleRoll}
                    disabled={primos < 100 || isRolling}
                    className="w-full py-3 bg-pocket-bg border-2 border-type-poison/30 text-type-poison font-bold rounded-xl hover:bg-type-poison/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isRolling ? 'Rolling...' : primos >= 100 ? 'Single Roll (100)' : 'Not Enough Primos'}
                  </motion.button>
                )}

                <button
                  onClick={() => setGameState('menu')}
                  className="w-full py-3 text-pocket-text-light font-semibold hover:text-pocket-text transition-colors"
                >
                  Back to Menu
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6">
              {/* Result Display */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center"
              >
                {/* Rarity Badge */}
                <motion.div
                  initial={{ y: -20 }}
                  animate={{ y: 0 }}
                  className="mb-4"
                >
                  <span
                    className="inline-block px-4 py-2 rounded-full font-bold text-white text-lg"
                    style={{ backgroundColor: getRarityColor(rollResult.rarity) }}
                  >
                    {rollResult.rarity}!
                  </span>
                </motion.div>

                {/* Pokemon Display */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="mb-4 flex justify-center"
                >
                  {generatePokemonSprite(POKEMON[rollResult.pokemon]?.primaryType || 'Normal', rollResult.pokemon, 64, false)}
                </motion.div>

                <h3 className="text-2xl font-bold text-pocket-text mb-2">{rollResult.pokemon}</h3>

                {/* Limit Break Diamonds */}
                <div className="flex justify-center mb-2">
                  <LimitBreakDiamonds level={getPokemonLimitBreak(rollResult.pokemon)} size={14} />
                </div>

                {POKEMON[rollResult.pokemon] && (
                  <div className="flex justify-center mb-4">
                    <TypeBadge type={POKEMON[rollResult.pokemon].primaryType} size={18} />
                  </div>
                )}

                {/* Duplicate / Limit Break Result */}
                {rollResult.isDuplicate && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`rounded-xl p-3 mb-4 ${
                      rollResult.isMaxLimitBreak
                        ? 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200'
                        : 'bg-green-50 border border-green-200'
                    }`}
                  >
                    {rollResult.isMaxLimitBreak ? (
                      <div>
                        <p className="text-purple-700 font-bold">Max Limit Break!</p>
                        <p className="text-purple-600 text-sm">
                          +{rollResult.shardsAwarded || LIMIT_BREAK_SHARD_REWARDS[rollResult.rarity]} Limit Break Shards
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-green-700 font-bold">Limit Break Up!</p>
                        <p className="text-green-600 text-sm">
                          +5% Base Stats (Level {rollResult.limitBreakLevel})
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Actions */}
                <div className="space-y-3 mt-6">
                  <motion.button
                    whileHover={{ scale: primos >= 100 && !isRolling ? 1.02 : 1 }}
                    whileTap={{ scale: primos >= 100 && !isRolling ? 0.98 : 1 }}
                    onClick={async () => {
                      setRollResult(null);
                      if (primos >= 100) {
                        await handleRoll();
                      }
                    }}
                    disabled={primos < 100 || isRolling}
                    className="w-full pocket-btn-purple py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRolling ? 'Rolling...' : 'Roll Again'}
                  </motion.button>
                  <button
                    onClick={() => {
                      setRollResult(null);
                      if (isTutorialStep('pokemon-gacha')) {
                        setHasPulledDuringTutorial(true);
                      } else {
                        setGameState('menu');
                      }
                    }}
                    className="w-full py-3 text-pocket-text-light font-semibold hover:text-pocket-text transition-colors"
                  >
                    {isTutorialStep('pokemon-gacha') ? 'Continue Tutorial' : 'Back to Menu'}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Pokemon Detail Modal */}
      <AnimatePresence>
        {selectedPokemon && POKEMON[selectedPokemon] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPokemon(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-card-lg p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto relative"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedPokemon(null)}
                className="absolute top-4 right-4 p-2 text-pocket-text-light hover:text-pocket-text rounded-lg"
              >
                <X size={20} />
              </button>

              {(() => {
                const pokemon = POKEMON[selectedPokemon];
                const limitBreak = getPokemonLimitBreak(selectedPokemon);
                const rarity = Object.entries(GACHA_RARITY).find(([_, data]) =>
                  data.pokemon.includes(selectedPokemon)
                )?.[0] || 'Common';

                return (
                  <>
                    {/* Pokemon sprite and name */}
                    <div className="text-center mb-4">
                      <div className="mb-3 flex justify-center">
                        {generatePokemonSprite(pokemon.primaryType || 'Normal', selectedPokemon, 64, false)}
                      </div>
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white inline-block mb-2"
                        style={{ backgroundColor: getRarityColor(rarity) }}
                      >
                        {rarity}
                      </span>
                      <h3 className="font-bold text-xl text-pocket-text">{selectedPokemon}</h3>
                      <div className="flex justify-center gap-2 mt-2">
                        <TypeBadge type={pokemon.primaryType} size={16} />
                        {pokemon.secondaryType && <TypeBadge type={pokemon.secondaryType} size={16} />}
                      </div>
                      <div className="flex justify-center mt-2">
                        <LimitBreakDiamonds level={limitBreak} size={14} />
                      </div>
                      {limitBreak > 0 && (
                        <p className="text-sm text-pocket-green font-semibold mt-1">
                          +{limitBreak * 5}% Base Stats
                        </p>
                      )}
                    </div>

                    {/* Base Stats */}
                    <div className="bg-pocket-bg rounded-xl p-3 mb-3">
                      <h4 className="font-bold text-pocket-text text-sm mb-2">Base Stats</h4>
                      <div className="space-y-1.5 text-xs">
                        {Object.entries(pokemon.baseStats).map(([stat, value]) => (
                          <div key={stat} className="flex items-center gap-2">
                            <span className="text-pocket-text-light w-16">{stat}</span>
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full bg-pocket-blue"
                                style={{ width: `${Math.min(100, (value / 200) * 100)}%` }}
                              />
                            </div>
                            <span className="font-bold text-pocket-text w-8 text-right">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Type Aptitudes */}
                    {pokemon.typeAptitudes && (
                      <div className="bg-blue-50 rounded-xl p-3 mb-3">
                        <h4 className="font-bold text-blue-700 text-sm mb-2">Type Aptitudes</h4>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          {Object.entries(pokemon.typeAptitudes).map(([color, grade]) => {
                            const colorToType = {
                              Red: 'Fire', Blue: 'Water', Green: 'Grass',
                              Yellow: 'Electric', Purple: 'Psychic', Orange: 'Fighting'
                            };
                            const typeName = colorToType[color] || color;
                            return (
                              <div
                                key={color}
                                className="flex justify-between items-center px-2 py-1 rounded bg-white"
                              >
                                <TypeIcon type={typeName} size={16} />
                                <span
                                  className="px-2 py-0.5 rounded text-white font-bold"
                                  style={{ backgroundColor: getAptitudeColor(grade) }}
                                >
                                  {grade}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Strategy Aptitudes */}
                    {pokemon.strategyAptitudes && (
                      <div className="bg-pocket-bg rounded-xl p-3">
                        <h4 className="font-bold text-pocket-text text-sm mb-2">Strategy Aptitudes</h4>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          {Object.entries(pokemon.strategyAptitudes).map(([strategy, grade]) => (
                            <div key={strategy} className="flex justify-between px-2 py-1 bg-white rounded">
                              <span className="text-pocket-text-light">{strategy}</span>
                              <span className="font-bold text-pocket-text">{grade}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Close button */}
              <button
                onClick={() => setSelectedPokemon(null)}
                className="w-full mt-4 py-2 rounded-xl bg-pocket-bg text-pocket-text-light font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tutorial overlay for pokemon-gacha step */}
      {showTutorialOverlay && isTutorialStep('pokemon-gacha') && (
        <TutorialOverlay
          stepId="pokemon-gacha"
          onContinue={() => {
            setShowTutorialOverlay(false);
          }}
        />
      )}

      {/* Gacha Pool Info Modal */}
      <GachaPoolInfoModal
        isOpen={showPoolInfo}
        onClose={() => setShowPoolInfo(false)}
        selectedGeneration={selectedGeneration}
      />
    </div>
  );
};

export default GachaScreen;
